import { useCallback, useEffect, useRef, useState } from "react";
import {
  getPremiumReceivableList,
  PremiumReceivableDto,
} from "utils/api/premiumReceivableApi";
import { PremiumReceivablesTransactionRecord } from "entities/UIModel/PremiumReceivablesTransactionRecord";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";

export const dtoToRecord = (
  dto: PremiumReceivableDto
): PremiumReceivablesTransactionRecord => ({
  ...dto,
  uniqueIdentifier: String(dto.PremiumReceivableID),
});

// Drop empty/null entries; flatten arrays to comma-joined strings. Numbers and
// booleans are passed through as-is rather than stringified, since the backend
// binds those criteria fields to bool/int, not string. Mirrors the same helper
// in usePolicyTransactionList.
export function serializeFilters(
  filters: Record<string, unknown>
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v == null) continue;
    if (Array.isArray(v)) {
      if (v.length === 0) continue;
      out[k] = v.join(",");
    } else if (typeof v === "number" || typeof v === "boolean") {
      out[k] = v;
    } else if (typeof v === "string" && v.trim() !== "") {
      out[k] = v;
    }
  }
  return out;
}

export function usePremiumReceivableList(
  listCount: number,
  requestedPage: number,
  sortColumn: string,
  filters: Record<string, unknown> = {},
  enabled: boolean = true
) {
  const [{ profileSettings }] = useProfileSettingsCache();
  const userTimeZone = profileSettings?.userTimeZone ?? "";
  const [records, setRecords] = useState<PremiumReceivablesTransactionRecord[]>([]);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [reloadToken, setReloadToken] = useState(0);
  const reqIdRef = useRef(0);

  const refetch = useCallback(() => setReloadToken((t) => t + 1), []);

  useEffect(() => {
    if (!enabled) {
      // Discard anything already in flight. Nothing else bumps the id on this
      // path, so a response landing after Reset would otherwise repopulate the
      // grid we just cleared, and its spinner would never resolve.
      reqIdRef.current++;
      setIsLoading(false);
      // Reset closes the gate, so drop the previous result set — otherwise the
      // grid keeps showing stale rows and never falls back to its empty state.
      // The functional update and already-zero primitives let React bail out,
      // so this cannot loop.
      setRecords((prev) => (prev.length ? [] : prev));
      setTotalRows(0);
      setTotalPages(0);
      return;
    }
    if (!userTimeZone) return;
    const myReq = ++reqIdRef.current;
    setIsLoading(true);
    getPremiumReceivableList({
      UserTimeZone: userTimeZone,
      RequestedPage: requestedPage,
      ListCount: listCount,
      SortColumn: sortColumn,
      ...serializeFilters(filters),
    })
      .then((dtos) => {
        if (reqIdRef.current !== myReq) return;
        // TotalRows / TotalPages are whole-query totals (not per-page)
        // duplicated on every row by the SP's window functions.
        const first = dtos[0];
        setTotalRows(first?.TotalRows ?? dtos.length);
        setTotalPages(first?.TotalPages ?? (dtos.length > 0 ? 1 : 0));
        setRecords(dtos.map(dtoToRecord));
        setIsLoading(false);
      })
      .catch(() => {
        if (reqIdRef.current === myReq) setIsLoading(false);
      });
  }, [listCount, requestedPage, sortColumn, userTimeZone, filters, enabled, reloadToken]);

  return { records, totalRows, totalPages, isLoading, setRecords, refetch };
}

export default usePremiumReceivableList;
